import type { LicenseCostRecord } from '@/features/licenses/LicensesApiService';
import type { ProviderCostGroup } from '@/features/licenses/LicenseCostPageViewModel';

/** Sentinel model id for the "no model" bucket (flat seat/subscription costs). */
export const SEATS_BUCKET_ID = '__seats__';
export const SEATS_BUCKET_LABEL = 'Seats & subscriptions';

export interface SpendCostRow {
    kind: 'provider' | 'model';
    id: string;
    label: string;
    /** Dimmed subheader under the label — the provider type on provider rows. */
    subLabel?: string;
    total: number;
    /** Share 0–1: of the grand total (provider rows) or of the parent provider (model rows). */
    share: number;
    /** Bar fill 0–100, peak-relative within the row's level. */
    widthPct: number;
    lineCount: number;
    connectionId: string;
    /** Set on model rows; the sentinel {@link SEATS_BUCKET_ID} for the no-model bucket. */
    modelId?: string;
    children?: SpendCostRow[];
}

function widthOf(value: number, peak: number): number {
    if (peak <= 0) return 0;
    return Math.max(2, Math.round((value / peak) * 100));
}

/** A provider's spend broken down by model, with all no-model (seat/subscription) records folded into one bucket. */
function buildModelRows(group: ProviderCostGroup): SpendCostRow[] {
    const byModel = new Map<string, LicenseCostRecord[]>();
    for (const record of group.records) {
        const model = record.modelId ?? '';
        const key = model.length > 0 ? model : SEATS_BUCKET_ID;
        const bucket = byModel.get(key);
        if (bucket === undefined) byModel.set(key, [record]);
        else bucket.push(record);
    }
    const entries = [...byModel.entries()].map(([modelId, records]) => ({
        modelId,
        total: records.reduce((sum, record) => sum + record.amount, 0),
        lineCount: records.length,
    }));
    const peak = entries.reduce((max, entry) => Math.max(max, entry.total), 0);
    return entries
        .sort((a, b) => b.total - a.total)
        .map((entry) => ({
            kind: 'model' as const,
            id: `${group.connectionId}/${entry.modelId}`,
            label: entry.modelId === SEATS_BUCKET_ID ? SEATS_BUCKET_LABEL : entry.modelId,
            total: entry.total,
            share: group.total > 0 ? entry.total / group.total : 0,
            widthPct: widthOf(entry.total, peak),
            lineCount: entry.lineCount,
            connectionId: group.connectionId,
            modelId: entry.modelId,
        }));
}

/** Builds the Cost-by-Provider ranked rows: one provider row per group, each expandable to its by-model breakdown. */
export function buildProviderCostRows(providerCosts: ProviderCostGroup[], grandTotal: number): SpendCostRow[] {
    const peak = providerCosts.reduce((max, group) => Math.max(max, group.total), 0);
    return providerCosts.map((group) => ({
        kind: 'provider' as const,
        id: group.connectionId,
        label: group.displayName.length > 0 ? group.displayName : group.typeLabel,
        subLabel: group.typeLabel,
        total: group.total,
        share: grandTotal > 0 ? group.total / grandTotal : 0,
        widthPct: widthOf(group.total, peak),
        lineCount: group.records.length,
        connectionId: group.connectionId,
        children: buildModelRows(group),
    }));
}
